import { useState } from "react";
import { GripVertical, Link2 } from "lucide-react";
import { footerHtml } from "@/lib/emailBlocks";

const fontA = "Arial, Helvetica, sans-serif";

function Ed({ value, onCommit, style, as: Tag = "div", testId }) {
  return (
    <Tag
      contentEditable
      suppressContentEditableWarning
      data-testid={testId}
      spellCheck={false}
      style={{ outline: "none", cursor: "text", minWidth: 10, ...style }}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        const t = e.currentTarget.innerText;
        if (t !== value) onCommit(t);
      }}
    >
      {value}
    </Tag>
  );
}

function Card({ item, block, onItem, onSelectBlock }) {
  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, padding: 10, textAlign: "center", background: "#fff", flex: 1, minWidth: 0 }}>
      {item.image ? (
        <img src={item.image} alt="" style={{ width: "100%", borderRadius: 8, display: "block", cursor: "pointer" }} onClick={onSelectBlock} />
      ) : (
        <div onClick={onSelectBlock} style={{ padding: "36px 0", background: "#F3F4F6", borderRadius: 8, color: "#9CA3AF", fontFamily: fontA, fontSize: 12, cursor: "pointer" }}>
          Image — set in panel
        </div>
      )}
      <Ed value={item.title} onCommit={(v) => onItem({ title: v })} testId="canvas-card-title" style={{ fontFamily: fontA, fontSize: 14, fontWeight: 600, color: "#111827", margin: "10px 0 0" }} />
      {item.price !== undefined && item.price !== "" && (
        <div style={{ margin: "2px 0 0", fontFamily: fontA, fontSize: 15, fontWeight: 700, color: "#111827", display: "flex", gap: 6, justifyContent: "center", alignItems: "baseline" }}>
          <Ed value={item.price} onCommit={(v) => onItem({ price: v })} testId="canvas-card-price" as="span" />
          {item.old_price ? (
            <Ed value={item.old_price} onCommit={(v) => onItem({ old_price: v })} as="span" style={{ color: "#9CA3AF", fontWeight: 400, textDecoration: "line-through", fontSize: 12 }} />
          ) : null}
        </div>
      )}
      {item.button ? (
        <div style={{ marginTop: 10 }}>
          <span style={{ display: "inline-block", background: block.color || "#6366F1", color: "#fff", padding: "8px 18px", borderRadius: 6, fontFamily: fontA, fontSize: 13, fontWeight: 600 }}>
            <Ed value={item.button} onCommit={(v) => onItem({ button: v })} as="span" testId="canvas-card-button" />
          </span>
        </div>
      ) : null}
    </div>
  );
}

function BlockView({ block, onChange, onItem, onSelect }) {
  const t = block.type;
  if (t === "heading")
    return <Ed as="h2" testId="canvas-heading" value={block.text} onCommit={(v) => onChange({ text: v })} style={{ margin: "0 0 8px", fontSize: block.size || 24, fontWeight: 700, color: "#111827", textAlign: block.align || "left", fontFamily: fontA }} />;
  if (t === "text")
    return <Ed as="p" testId="canvas-text" value={block.text} onCommit={(v) => onChange({ text: v })} style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.6, color: "#374151", textAlign: block.align || "left", fontFamily: fontA, whiteSpace: "pre-wrap" }} />;
  if (t === "image")
    return block.url ? (
      <div style={{ textAlign: "center", margin: "0 0 12px", position: "relative" }}>
        <img src={block.url} alt={block.alt || ""} onClick={onSelect} style={{ width: block.width ? parseInt(block.width) : undefined, maxWidth: "100%", borderRadius: 6, cursor: "pointer", display: "inline-block" }} />
        {block.link ? <span title={block.link} style={{ position: "absolute", top: 6, right: 6, background: "rgba(17,24,39,0.75)", color: "#fff", borderRadius: 4, padding: "2px 5px", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontFamily: fontA }}><Link2 size={10} /> linked</span> : null}
      </div>
    ) : (
      <div onClick={onSelect} style={{ margin: "0 0 12px", padding: 32, background: "#F3F4F6", border: "1px dashed #D1D5DB", borderRadius: 6, textAlign: "center", color: "#9CA3AF", fontFamily: fontA, fontSize: 13, cursor: "pointer" }}>
        Image placeholder — click, then add URL or upload in the panel
      </div>
    );
  if (t === "button")
    return (
      <div style={{ textAlign: block.align || "center", margin: "8px 0 16px" }}>
        <span style={{ display: "inline-block", background: block.color || "#6366F1", color: "#fff", padding: "12px 28px", borderRadius: 6, fontWeight: 600, fontFamily: fontA, fontSize: 15 }}>
          <Ed as="span" testId="canvas-button" value={block.text} onCommit={(v) => onChange({ text: v })} />
        </span>
      </div>
    );
  if (t === "images") {
    const items = block.items || [];
    return (
      <div onClick={onSelect} style={{ display: "flex", gap: 8, margin: "0 0 12px", cursor: "pointer" }} data-testid="canvas-image-row">
        {items.length === 0 && <div style={{ flex: 1, padding: 28, background: "#F3F4F6", border: "1px dashed #D1D5DB", borderRadius: 6, textAlign: "center", color: "#9CA3AF", fontFamily: fontA, fontSize: 13 }}>Image row — add images in the panel</div>}
        {items.map((it, i) => (
          <div key={i} style={{ flex: 1, minWidth: 0, position: "relative" }}>
            {it.url ? (
              <img src={it.url} alt={it.alt || ""} style={{ width: "100%", borderRadius: 6, display: "block" }} />
            ) : (
              <div style={{ padding: "30px 0", background: "#F3F4F6", border: "1px dashed #D1D5DB", borderRadius: 6, textAlign: "center", color: "#9CA3AF", fontFamily: fontA, fontSize: 11 }}>Image {i + 1}</div>
            )}
            {it.link ? <span title={it.link} style={{ position: "absolute", top: 4, right: 4, background: "rgba(17,24,39,0.75)", color: "#fff", borderRadius: 4, padding: "2px 5px", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9, fontFamily: fontA }}><Link2 size={9} /> linked</span> : null}
          </div>
        ))}
      </div>
    );
  }
  if (t === "divider") return <hr style={{ border: "none", borderTop: "1px solid #E5E7EB", margin: "16px 0" }} />;
  if (t === "html")
    return block.html ? (
      <div data-testid="canvas-html" onClick={onSelect} style={{ cursor: "pointer", margin: "0 0 12px" }} dangerouslySetInnerHTML={{ __html: block.html }} />
    ) : (
      <div onClick={onSelect} style={{ margin: "0 0 12px", padding: 24, background: "#F3F4F6", border: "1px dashed #D1D5DB", borderRadius: 6, textAlign: "center", color: "#9CA3AF", fontFamily: fontA, fontSize: 13, cursor: "pointer" }}>
        HTML block — paste your markup in the panel
      </div>
    );
  if (t === "spacer") return <div onClick={onSelect} style={{ height: block.height || 24, cursor: "pointer" }} />;
  if (t === "cards") {
    const cols = Math.max(1, Math.min(3, block.columns || 2));
    const rows = [];
    for (let i = 0; i < (block.items || []).length; i += cols) rows.push(block.items.slice(i, i + cols));
    return (
      <div style={{ margin: "0 0 12px" }}>
        {rows.map((row, ri) => (
          <div key={ri} style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            {row.map((item, ci) => (
              <Card key={ci} item={item} block={block} onSelectBlock={onSelect} onItem={(patch) => onItem(ri * cols + ci, patch)} />
            ))}
            {Array.from({ length: cols - row.length }).map((_, p) => <div key={`p${p}`} style={{ flex: 1 }} />)}
          </div>
        ))}
      </div>
    );
  }
  return null;
}

export default function EditablePreview({ blocks, selected, setSelected, updateBlock, updateCardItem, footer, onReorder, onDropFiles }) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [fileOver, setFileOver] = useState(false);

  return (
    <div
      style={{ background: "#F3F4F6", padding: "24px 12px", borderRadius: 8, outline: fileOver ? "2px dashed #6366F1" : "none" }}
      data-testid="editable-canvas"
      onDragOver={(e) => {
        e.preventDefault();
        if (e.dataTransfer.types?.includes("Files")) setFileOver(true);
      }}
      onDragLeave={() => setFileOver(false)}
      onDrop={(e) => {
        setFileOver(false);
        if (e.dataTransfer.files?.length) {
          e.preventDefault();
          onDropFiles && onDropFiles(e.dataTransfer.files);
        }
      }}
    >
      {fileOver && (
        <div style={{ textAlign: "center", fontFamily: fontA, fontSize: 12, color: "#6366F1", marginBottom: 8 }}>Drop images to add them to the email</div>
      )}
      <div style={{ maxWidth: 600, margin: "0 auto", background: "#fff", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: 32 }}>
        {blocks.map((b) => (
          <div
            key={b.id}
            data-testid="canvas-block"
            onClick={() => setSelected(b.id)}
            onDragOver={(e) => {
              if (dragId) {
                e.preventDefault();
                setOverId(b.id);
              }
            }}
            onDrop={(e) => {
              if (dragId && dragId !== b.id) {
                e.preventDefault();
                e.stopPropagation();
                onReorder && onReorder(dragId, b.id);
              }
              setDragId(null);
              setOverId(null);
            }}
            style={{
              position: "relative",
              borderRadius: 4,
              transition: "box-shadow 0.15s",
              boxShadow: selected === b.id ? "0 0 0 2px rgba(99,102,241,0.6)" : "none",
              borderTop: overId === b.id && dragId && dragId !== b.id ? "2px solid #6366F1" : "2px solid transparent",
              opacity: dragId === b.id ? 0.4 : 1,
            }}
            className="group/blk"
          >
            <span
              data-testid="canvas-drag-handle"
              draggable
              onDragStart={(e) => {
                setDragId(b.id);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", b.id);
              }}
              onDragEnd={() => {
                setDragId(null);
                setOverId(null);
              }}
              title="Drag to reorder"
              style={{ position: "absolute", left: -26, top: "50%", transform: "translateY(-50%)", cursor: "grab", color: "#9CA3AF", padding: 4, opacity: 0.85 }}
            >
              <GripVertical size={14} />
            </span>
            <BlockView
              block={b}
              onSelect={() => setSelected(b.id)}
              onChange={(patch) => updateBlock(b.id, patch)}
              onItem={(idx, patch) => updateCardItem(b.id, idx, patch)}
            />
          </div>
        ))}
        </div>
        <div data-testid="canvas-footer" dangerouslySetInnerHTML={{ __html: footerHtml(footer) }} />
      </div>
    </div>
  );
}
